import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

const ORIGIN_FIELDS = [
  ["ship_from_name", "Name"], ["ship_from_company", "Company"],
  ["ship_from_address1", "Address line 1"], ["ship_from_address2", "Address line 2"],
  ["ship_from_city", "City"], ["ship_from_state", "Province/State (2-letter)"],
  ["ship_from_zip", "Postal code"], ["ship_from_country", "Country (2-letter)"],
  ["ship_from_phone", "Phone"], ["ship_from_email", "Email"],
];

// Admin: ShipNerd origin address, API token rotation, and reusable package library.
export default function ShippingSettingsCard() {
  const [origin, setOrigin] = useState({});
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState("");
  const [lib, setLib] = useState([]);
  const [newPkg, setNewPkg] = useState({ name: "", weight: 5, length: 12, width: 12, height: 6 });

  const loadCfg = () => api.get("/shipping/config").then((r) => { setCfg(r.data); setOrigin(r.data.origin || {}); }).catch(() => {});
  const loadLib = () => api.get("/shipping/packages").then((r) => setLib(r.data || [])).catch(() => {});
  useEffect(() => { loadCfg(); loadLib(); }, []);

  const saveOrigin = async () => {
    try { await api.put("/settings", origin); toast.success("Ship-from address saved"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };
  const saveToken = async () => {
    if (!token.trim()) return toast.error("Paste your ShipNerd API token");
    try { await api.put("/shipping/config/token", { token: token.trim() }); toast.success("ShipNerd token updated"); setToken(""); loadCfg(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };
  const addPkg = async () => {
    if (!newPkg.name.trim()) return toast.error("Package name required");
    try {
      await api.post("/shipping/packages", { name: newPkg.name.trim(), weight: +newPkg.weight || 0, length: +newPkg.length || 0, width: +newPkg.width || 0, height: +newPkg.height || 0 });
      toast.success("Package added"); setNewPkg({ name: "", weight: 5, length: 12, width: 12, height: 6 }); loadLib();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };
  const delPkg = async (id) => { try { await api.delete(`/shipping/packages/${id}`); loadLib(); } catch (e) { toast.error(e.message); } };

  return (
    <div className="space-y-6" data-testid="shipping-settings">
      {/* Connection status */}
      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="ship-connection-card">
        <h3 className="font-head font-bold mb-1">ShipNerd — Live shipping rates</h3>
        <p className="text-xs text-slate-500 mb-4">Real-time rates from Canada Post / UPS / DHL and more, shown in the Services → Delivery block of every module.</p>
        {cfg && (
          <div className={`flex items-start gap-2 rounded-sm border p-3 mb-4 text-xs ${cfg.configured && !cfg.token_expired ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid="ship-status">
            {cfg.configured && !cfg.token_expired ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> : <AlertTriangle size={15} className="mt-0.5 shrink-0" />}
            <div>
              {cfg.configured ? (
                <>
                  <div className="font-semibold">Token installed{cfg.token_expired ? " — EXPIRED" : ""}</div>
                  <div className="opacity-80">{cfg.token_masked}{cfg.token_exp ? ` · expires ${new Date(cfg.token_exp).toLocaleString()}` : ""}</div>
                  <div className="opacity-80 mt-1">Note: ShipNerd must approve your account for API access before live rates work.</div>
                </>
              ) : <div className="font-semibold">No token installed — paste your ShipNerd API token below.</div>}
            </div>
          </div>
        )}
        <Label className="text-xs">ShipNerd API token (JWT)</Label>
        <div className="flex gap-2 mt-1">
          <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste a fresh token from ShipNerd → Account Settings → API" data-testid="ship-token-input" className="rounded-sm text-xs font-mono" />
          <Button onClick={saveToken} data-testid="ship-token-save" className="rounded-sm bg-[#2495D3] hover:bg-[#1E7AA9] shrink-0"><Save size={15} className="mr-1.5" />Save token</Button>
        </div>
      </div>

      {/* Origin address */}
      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="ship-origin-card">
        <div className="flex items-center justify-between mb-1 gap-3">
          <h3 className="font-head font-bold">Ship-from address (your shop)</h3>
          <Button onClick={saveOrigin} data-testid="ship-origin-save" variant="outline" className="rounded-sm h-8 text-xs"><Save size={14} className="mr-1" />Save</Button>
        </div>
        <p className="text-xs text-slate-500 mb-4">This is the origin used for every live rate quote.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {ORIGIN_FIELDS.map(([k, l]) => (
            <div key={k}>
              <Label className="text-xs">{l}</Label>
              <Input value={origin[k] || ""} onChange={(e) => setOrigin({ ...origin, [k]: e.target.value })} data-testid={`origin-${k}`} className="rounded-sm mt-1 text-sm" />
            </div>
          ))}
        </div>
      </div>

      {/* Package library */}
      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="ship-library-card">
        <h3 className="font-head font-bold mb-1">Package library</h3>
        <p className="text-xs text-slate-500 mb-4">Reusable box presets (weight &amp; dimensions). Available as quick-picks when quoting shipping. You can also save new boxes directly from the Delivery block.</p>
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-2 items-end mb-3">
          <div><Label className="text-[10px] uppercase text-slate-400">Name</Label><Input value={newPkg.name} onChange={(e) => setNewPkg({ ...newPkg, name: e.target.value })} data-testid="lib-new-name" className="rounded-sm h-8 text-xs mt-0.5" /></div>
          <div><Label className="text-[10px] uppercase text-slate-400">Wt lb</Label><Input type="number" value={newPkg.weight} onChange={(e) => setNewPkg({ ...newPkg, weight: e.target.value })} data-testid="lib-new-weight" className="rounded-sm h-8 w-16 text-xs num text-right mt-0.5" /></div>
          <div><Label className="text-[10px] uppercase text-slate-400">L in</Label><Input type="number" value={newPkg.length} onChange={(e) => setNewPkg({ ...newPkg, length: e.target.value })} data-testid="lib-new-length" className="rounded-sm h-8 w-14 text-xs num text-right mt-0.5" /></div>
          <div><Label className="text-[10px] uppercase text-slate-400">W in</Label><Input type="number" value={newPkg.width} onChange={(e) => setNewPkg({ ...newPkg, width: e.target.value })} data-testid="lib-new-width" className="rounded-sm h-8 w-14 text-xs num text-right mt-0.5" /></div>
          <div><Label className="text-[10px] uppercase text-slate-400">H in</Label><Input type="number" value={newPkg.height} onChange={(e) => setNewPkg({ ...newPkg, height: e.target.value })} data-testid="lib-new-height" className="rounded-sm h-8 w-14 text-xs num text-right mt-0.5" /></div>
          <Button onClick={addPkg} data-testid="lib-add" variant="outline" className="rounded-sm h-8 text-xs"><Plus size={14} className="mr-1" />Add</Button>
        </div>
        <div className="divide-y divide-slate-100">
          {lib.map((p) => (
            <div key={p.id} data-testid="lib-row" className="flex items-center justify-between py-2 text-sm">
              <span className="font-medium">{p.name}</span>
              <span className="text-slate-500 num text-xs">{p.weight} lb · {p.length}×{p.width}×{p.height} in</span>
              <button onClick={() => delPkg(p.id)} data-testid="lib-delete" className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}
          {lib.length === 0 && <div className="py-4 text-center text-slate-400 text-xs">No saved packages yet.</div>}
        </div>
      </div>
    </div>
  );
}
